import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Column type defaults:
 *   text     → asc first (A→Z)
 *   status   → asc first (A→Z)
 *   date     → desc first (newest)
 *   number   → desc first (highest)
 *   currency → desc first (highest)
 *   percent  → desc first (highest)
 */
const DESC_FIRST_TYPES = new Set(['date', 'number', 'currency', 'percent']);

function getDefaultDir(type) {
  return DESC_FIRST_TYPES.has(type) ? 'desc' : 'asc';
}

function compare(a, b, key, type) {
  let va = a?.[key];
  let vb = b?.[key];

  // Nulls always to bottom
  const aN = va == null || va === '';
  const bN = vb == null || vb === '';
  if (aN && bN) return 0;
  if (aN) return 1;
  if (bN) return -1;

  switch (type) {
    case 'number':
    case 'currency':
    case 'percent':
      return (Number(va) || 0) - (Number(vb) || 0);
    case 'date':
      return new Date(va).getTime() - new Date(vb).getTime();
    case 'text':
    case 'status':
    default:
      return String(va).localeCompare(String(vb), undefined, { sensitivity: 'base' });
  }
}

/**
 * useSortableTable — generic sorting hook for any list page.
 *
 * @param {Array} data       — raw data array
 * @param {Object} options
 * @param {Array}  options.columns     — [{key, type}] column definitions
 * @param {string} options.defaultKey  — initial sort column key (optional)
 * @param {string} options.defaultDir  — initial sort direction (optional)
 * @param {boolean} options.persistUrl — sync sort state with URL query params (default false)
 *
 * @returns {{ sorted, sortKey, sortDir, onSort, clearSort }}
 */
export function useSortableTable(data, options = {}) {
  const { columns = [], defaultKey = null, defaultDir = null, persistUrl = false } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const urlKey = persistUrl ? searchParams?.get('sort') || defaultKey : defaultKey;
  const urlDir = persistUrl ? searchParams?.get('dir') || defaultDir : defaultDir;

  const [sortKey, setSortKey] = useState(urlKey);
  const [sortDir, setSortDir] = useState(urlDir);

  const onSort = useCallback((key) => {
    const col = columns.find(c => c.key === key);
    const type = col?.type || 'text';
    const defDir = getDefaultDir(type);

    let nextKey, nextDir;
    if (sortKey !== key) {
      nextKey = key;
      nextDir = defDir;
    } else if (sortDir === defDir) {
      nextKey = key;
      nextDir = defDir === 'asc' ? 'desc' : 'asc';
    } else {
      // Third click → clear
      nextKey = null;
      nextDir = null;
    }

    setSortKey(nextKey);
    setSortDir(nextDir);

    if (persistUrl && setSearchParams) {
      setSearchParams(prev => {
        const p = new URLSearchParams(prev);
        if (nextKey) {
          p.set('sort', nextKey);
          p.set('dir', nextDir);
        } else {
          p.delete('sort');
          p.delete('dir');
        }
        return p;
      }, { replace: true });
    }
  }, [sortKey, sortDir, columns, persistUrl, setSearchParams]);

  const clearSort = useCallback(() => {
    setSortKey(null);
    setSortDir(null);
    if (persistUrl && setSearchParams) {
      setSearchParams(prev => {
        const p = new URLSearchParams(prev);
        p.delete('sort');
        p.delete('dir');
        return p;
      }, { replace: true });
    }
  }, [persistUrl, setSearchParams]);

  const sorted = useMemo(() => {
    if (!sortKey || !data) return data || [];
    const col = columns.find(c => c.key === sortKey);
    const type = col?.type || 'text';
    const dir = sortDir === 'desc' ? -1 : 1;
    const result = [...data].sort((a, b) => {
      const va = a?.[sortKey];
      const vb = b?.[sortKey];
      // Nulls always to bottom, regardless of direction
      const aN = va == null || va === '';
      const bN = vb == null || vb === '';
      if (aN && bN) return 0;
      if (aN) return 1;
      if (bN) return -1;
      return dir * compare(a, b, sortKey, type);
    });
    return result;
  }, [data, sortKey, sortDir, columns]);

  return { sorted, sortKey, sortDir, onSort, clearSort };
}
